import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Briefcase, MapPin, DollarSign, Link as LinkIcon,
  Mail, Plus, X, ArrowRight, Zap, Building2, ChevronRight
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { useAuth, authHeaders } from "@/context/AuthContext";
import ResendVerificationLink from "@/components/ResendVerificationLink";
import { getErrorMessage, getErrorCode } from "@/lib/apiErrors";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const JOB_TYPES = ["full-time", "part-time", "contract", "remote", "hybrid"];
const STEPS = ["Position", "Details", "Location", "Apply", "Review"];

const empty = () => ({ zip_code: "", city: "", state: "" });

export default function PostAJob() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [errorCode, setErrorCode] = useState(null);

  const [form, setForm] = useState({
    title: "",
    company_name: user?.business_name || "",
    job_type: "full-time",
    description: "",
    salary_min: "",
    salary_max: "",
    salary_period: "year",
    locations: [empty()],
    apply_url: "",
    apply_email: "",
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addLocation = () => setForm(f => ({ ...f, locations: [...f.locations, empty()] }));
  const removeLocation = (i) => setForm(f => ({ ...f, locations: f.locations.filter((_, idx) => idx !== i) }));
  const setLoc = (i, k, v) => setForm(f => {
    const locs = [...f.locations];
    locs[i] = { ...locs[i], [k]: v };
    return { ...f, locations: locs };
  });

  const validate = () => {
    if (step === 0 && (!form.title.trim() || !form.company_name.trim())) return "Title and company name required";
    if (step === 1 && !form.description.trim()) return "Job description required";
    if (step === 2 && !form.locations[0]?.zip_code.trim()) return "At least one ZIP code required";
    if (step === 3 && !form.apply_url.trim() && !form.apply_email.trim()) return "Add an apply URL or email";
    return "";
  };

  const next = () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError("");
    setStep(s => s + 1);
  };
  const back = () => { setError(""); setStep(s => s - 1); };

  const submit = async () => {
    if (!user) { navigate("/login?redirect=/post-a-job"); return; }
    setLoading(true);
    setError("");
    try {
      const payload = {
        ...form,
        salary_min: form.salary_min ? parseInt(form.salary_min) : null,
        salary_max: form.salary_max ? parseInt(form.salary_max) : null,
        locations: form.locations.filter(l => l.zip_code),
        origin_url: window.location.origin,
      };
      const res = await axios.post(`${API}/jobs/checkout`, payload, {
        headers: authHeaders(),
      });
      window.location.href = res.data.url;
    } catch (e) {
      setError(getErrorMessage(e, "Something went wrong. Please try again."));
      setErrorCode(getErrorCode(e));
    }
    setLoading(false);
  };

  const inputClass = "w-full px-4 py-3 rounded-xl text-sm outline-none transition-all text-slate-700 placeholder-slate-400";
  const inputStyle = { border: "1.5px solid #e2e8f0", background: "white" };

  return (
    <div className="min-h-screen" style={{ background: "#060B14" }}>
      <SEOHead
        title="Post a Job"
        description="Post a job opening on the NextDayRefund.com job board. $50 per job post; listings run for 90 days."
        canonical="/post-a-job"
      />
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden py-10" style={{ borderBottom: "1px solid rgba(46,204,113,0.12)" }}>
        <div className="absolute inset-0 map-intel-grid pointer-events-none" />
        <div className="section-container relative text-center">
          <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full"
            style={{ background: "rgba(46,204,113,0.1)", border: "1px solid rgba(46,204,113,0.22)" }}>
            <Briefcase size={12} className="text-[#2ECC71]" />
            <span className="text-[#2ECC71] text-xs font-bold tracking-widest uppercase">90-Day Job Post · $50</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white mb-3">
            Post a Job.<br />
            <span style={{
              background: "linear-gradient(135deg, #2ECC71, #06b6d4)",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text"
            }}>Reach Local Talent.</span>
          </h1>
          <p className="text-slate-400 max-w-lg mx-auto text-sm">
            Your job is shown to thousands of local visitors already browsing for financial services. Auto-expires in 90 days.
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="section-container py-10">
        <div className="max-w-2xl mx-auto">

          {/* Progress Steps */}
          <div className="flex items-center justify-between mb-8">
            {STEPS.map((s, i) => (
              <React.Fragment key={s}>
                <div className="flex flex-col items-center gap-1">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-black transition-all duration-200"
                    style={i < step
                      ? { background: "#2ECC71", color: "white" }
                      : i === step
                        ? { background: "rgba(46,204,113,0.15)", color: "#2ECC71", border: "2px solid #2ECC71" }
                        : { background: "rgba(255,255,255,0.05)", color: "#475569", border: "1px solid rgba(255,255,255,0.1)" }}>
                    {i < step ? "✓" : i + 1}
                  </div>
                  <span className="text-xs font-semibold hidden sm:block"
                    style={{ color: i === step ? "#2ECC71" : i < step ? "#94a3b8" : "#475569" }}>
                    {s}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className="flex-1 h-px mx-2 transition-all duration-300"
                    style={{ background: i < step ? "#2ECC71" : "rgba(255,255,255,0.08)" }} />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Card */}
          <div className="rounded-2xl p-7"
            style={{
              background: "rgba(12,19,32,0.95)",
              border: "1px solid rgba(255,255,255,0.08)",
              boxShadow: "0 8px 40px rgba(0,0,0,0.4)"
            }}>

            {/* Step 0: Position */}
            {step === 0 && (
              <div className="space-y-4">
                <h2 className="text-xl font-black text-white mb-5">Job Position</h2>
                <div>
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1.5 block">Job Title *</label>
                  <input value={form.title} onChange={e => set("title", e.target.value)}
                    placeholder="e.g. Senior Tax Accountant"
                    className={inputClass} style={inputStyle}
                    data-testid="job-title-input" />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1.5 block">Company Name *</label>
                  <input value={form.company_name} onChange={e => set("company_name", e.target.value)}
                    placeholder="Your company or business name"
                    className={inputClass} style={inputStyle}
                    data-testid="job-company-input" />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1.5 block">Job Type *</label>
                  <div className="flex flex-wrap gap-2">
                    {JOB_TYPES.map(t => (
                      <button key={t} onClick={() => set("job_type", t)}
                        className="px-4 py-2 rounded-full text-sm font-semibold transition-all capitalize"
                        style={form.job_type === t
                          ? { background: "rgba(46,204,113,0.15)", color: "#2ECC71", border: "1.5px solid #2ECC71" }
                          : { background: "rgba(255,255,255,0.04)", color: "#64748b", border: "1px solid rgba(255,255,255,0.1)" }}>
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 1: Details */}
            {step === 1 && (
              <div className="space-y-4">
                <h2 className="text-xl font-black text-white mb-5">Job Details</h2>
                <div>
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1.5 block">Full Description *</label>
                  <textarea value={form.description} onChange={e => set("description", e.target.value)}
                    placeholder="Describe the role, responsibilities, requirements, and why someone should want to work here. The more detail, the better candidates you'll attract."
                    rows={8}
                    className={inputClass} style={{ ...inputStyle, resize: "vertical" }}
                    data-testid="job-description-input" />
                  <p className="text-slate-600 text-xs mt-1">{form.description.length} characters</p>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1.5 block">Salary Range (Optional)</label>
                  <div className="flex items-center gap-3">
                    <div className="relative flex-1">
                      <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input type="number" value={form.salary_min} onChange={e => set("salary_min", e.target.value)}
                        placeholder="Min"
                        className={inputClass + " pl-8"} style={inputStyle}
                        data-testid="job-salary-min" />
                    </div>
                    <span className="text-slate-500 text-sm">to</span>
                    <div className="relative flex-1">
                      <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input type="number" value={form.salary_max} onChange={e => set("salary_max", e.target.value)}
                        placeholder="Max"
                        className={inputClass + " pl-8"} style={inputStyle}
                        data-testid="job-salary-max" />
                    </div>
                    <select value={form.salary_period} onChange={e => set("salary_period", e.target.value)}
                      className={inputClass + " w-24"} style={inputStyle}>
                      <option value="year">/ year</option>
                      <option value="hour">/ hour</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Location */}
            {step === 2 && (
              <div className="space-y-4">
                <h2 className="text-xl font-black text-white mb-5">Job Location(s)</h2>
                <p className="text-slate-400 text-sm">Add all locations where this job is available. Each ZIP code gets its own listing on local search pages.</p>
                <div className="space-y-3">
                  {form.locations.map((loc, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <div className="flex-1 grid grid-cols-3 gap-2">
                        <input value={loc.zip_code} onChange={e => setLoc(i, "zip_code", e.target.value)}
                          placeholder="ZIP Code"
                          className={inputClass} style={inputStyle}
                          data-testid={`job-zip-${i}`} />
                        <input value={loc.city} onChange={e => setLoc(i, "city", e.target.value)}
                          placeholder="City"
                          className={inputClass} style={inputStyle} />
                        <input value={loc.state} onChange={e => setLoc(i, "state", e.target.value.toUpperCase().slice(0, 2))}
                          placeholder="ST"
                          maxLength={2}
                          className={inputClass} style={inputStyle} />
                      </div>
                      {form.locations.length > 1 && (
                        <button onClick={() => removeLocation(i)}
                          className="mt-3 text-slate-500 hover:text-rose-400 transition-colors">
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button onClick={addLocation}
                  className="flex items-center gap-2 text-sm font-semibold transition-all py-2 px-3 rounded-lg"
                  style={{ color: "#2ECC71", border: "1px dashed rgba(46,204,113,0.4)" }}
                  data-testid="add-location-btn">
                  <Plus size={14} /> Add Another Location
                </button>
              </div>
            )}

            {/* Step 3: Apply */}
            {step === 3 && (
              <div className="space-y-4">
                <h2 className="text-xl font-black text-white mb-5">How to Apply</h2>
                <p className="text-slate-400 text-sm">Provide at least one way for candidates to apply. Both work together.</p>
                <div>
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1.5 block">Apply URL</label>
                  <div className="relative">
                    <LinkIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input value={form.apply_url} onChange={e => set("apply_url", e.target.value)}
                      placeholder="https://yourcompany.com/careers/job-id"
                      className={inputClass + " pl-8"} style={inputStyle}
                      data-testid="job-apply-url" />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px" style={{ background: "#e2e8f0" }} />
                  <span className="text-slate-500 text-xs font-bold">OR</span>
                  <div className="flex-1 h-px" style={{ background: "#e2e8f0" }} />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1.5 block">Apply Email</label>
                  <div className="relative">
                    <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input value={form.apply_email} onChange={e => set("apply_email", e.target.value)}
                      placeholder="careers@yourcompany.com"
                      type="email"
                      className={inputClass + " pl-8"} style={inputStyle}
                      data-testid="job-apply-email" />
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Review */}
            {step === 4 && (
              <div className="space-y-4">
                <h2 className="text-xl font-black text-white mb-5">Review & Pay</h2>
                <div className="rounded-xl p-4 space-y-3"
                  style={{ background: "rgba(8,12,20,0.8)", border: "1px solid rgba(255,255,255,0.07)" }}>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ background: "rgba(46,204,113,0.1)", border: "1px solid rgba(46,204,113,0.2)" }}>
                      <Building2 size={18} className="text-[#2ECC71]" />
                    </div>
                    <div>
                      <p className="text-white font-bold">{form.title}</p>
                      <p className="text-slate-400 text-sm">{form.company_name}</p>
                    </div>
                    <span className="ml-auto text-xs font-bold px-2.5 py-1 rounded-full capitalize"
                      style={{ background: "rgba(46,204,113,0.1)", color: "#2ECC71" }}>
                      {form.job_type}
                    </span>
                  </div>
                  {form.description && (
                    <p className="text-slate-400 text-sm line-clamp-3 leading-relaxed">
                      {form.description.slice(0, 200)}{form.description.length > 200 ? "…" : ""}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-3 pt-1">
                    {form.locations.filter(l => l.zip_code).map((l, i) => (
                      <span key={i} className="flex items-center gap-1 text-xs text-slate-400">
                        <MapPin size={10} /> {l.city ? `${l.city}, ${l.state}` : l.zip_code}
                      </span>
                    ))}
                    {(form.salary_min || form.salary_max) && (
                      <span className="flex items-center gap-1 text-xs text-slate-400">
                        <DollarSign size={10} />
                        {form.salary_min && `$${Number(form.salary_min).toLocaleString()}`}
                        {form.salary_min && form.salary_max && " – "}
                        {form.salary_max && `$${Number(form.salary_max).toLocaleString()}`}
                        /{form.salary_period}
                      </span>
                    )}
                  </div>
                </div>

                {/* Pricing box */}
                <div className="rounded-xl p-4"
                  style={{ background: "rgba(46,204,113,0.06)", border: "1px solid rgba(46,204,113,0.2)" }}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-bold">Job Post — 90-Day Listing</p>
                      <p className="text-slate-400 text-sm mt-0.5">Visible immediately after payment · auto-deletes after 90 days</p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-black text-[#2ECC71]">$50</p>
                      <p className="text-slate-400 text-xs">one-time</p>
                    </div>
                  </div>
                </div>

                {!user && (
                  <div className="rounded-xl p-3 text-sm font-semibold text-center"
                    style={{ background: "rgba(250,204,21,0.08)", border: "1px solid rgba(250,204,21,0.2)", color: "#facc15" }}>
                    You'll be asked to log in or register before payment.
                  </div>
                )}
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="mt-4 rounded-xl px-4 py-3 text-sm font-semibold"
                style={{ background: "rgba(248,113,113,0.1)", color: "#f87171", border: "1px solid rgba(248,113,113,0.2)" }}>
                {error}
                {errorCode === "email_unverified" && (
                  <div className="mt-2">
                    <ResendVerificationLink email={user?.email} className="underline font-semibold" />
                  </div>
                )}
              </div>
            )}

            {/* Nav buttons */}
            <div className="flex items-center justify-between mt-6 pt-5"
              style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
              {step > 0
                ? <button onClick={back} className="text-slate-400 hover:text-white text-sm font-semibold flex items-center gap-1 transition-colors">
                    ← Back
                  </button>
                : <Link to="/jobs" className="text-slate-500 hover:text-slate-300 text-sm transition-colors">
                    Browse Jobs
                  </Link>
              }

              {step < STEPS.length - 1 ? (
                <button onClick={next}
                  className="flex items-center gap-2 font-bold px-6 py-3 rounded-xl text-sm text-white transition-all hover:scale-105"
                  style={{ background: "linear-gradient(135deg, #2ECC71, #16a34a)", boxShadow: "0 4px 18px rgba(46,204,113,0.3)" }}
                  data-testid="job-form-next-btn">
                  Next: {STEPS[step + 1]} <ChevronRight size={15} />
                </button>
              ) : (
                <button onClick={submit} disabled={loading}
                  className="flex items-center gap-2 font-bold px-6 py-3 rounded-xl text-sm text-white transition-all hover:scale-105 disabled:opacity-50"
                  style={{ background: "linear-gradient(135deg, #2ECC71, #16a34a)", boxShadow: "0 4px 20px rgba(46,204,113,0.35)" }}
                  data-testid="job-submit-btn">
                  {loading ? "Redirecting..." : <><Zap size={15} /> Pay $50 &amp; Publish</>}
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
